import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bookmark, BookmarkX, MessageCircle, ShieldOff } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

/**
 * Private per-user Saved Messages view. Only ever displays the caller's
 * OWN bookmarks. Content bodies are hydrated server-side; if the caller
 * has lost Circle access, the row appears as `unavailable=true` and
 * the body is intentionally blank.
 */
export default function SavedMessages() {
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const load = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (query.trim()) params.q = query.trim().slice(0, 100);
      const r = await api.get("/messages/bookmarks", { params });
      setBookmarks(r.data?.bookmarks || []);
    } catch (_) { toast.error("Could not load saved messages"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(""); }, [load]);
  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  const remove = async (mid) => {
    try {
      await api.delete(`/messages/${mid}/bookmark`);
      setBookmarks((rows) => rows.filter((r) => r.message_id !== mid));
      toast("Removed from Saved");
    } catch (_) { toast.error("Could not remove bookmark"); }
  };

  return (
    <div className="max-w-3xl mx-auto p-8" data-testid="saved-messages-page">
      <p className="text-xs uppercase tracking-[0.25em] text-primary">Personal library</p>
      <h1 className="font-serif text-4xl font-black tracking-tight mt-1">Saved Messages</h1>
      <p className="text-sm text-muted-foreground mt-2">
        Private to you. Circle moderators, message authors, and other members never see what you've saved.
      </p>
      <div className="mt-4 relative">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search saved messages"
          maxLength={100}
          className="w-full bg-secondary border border-border rounded-full px-4 py-2 text-sm pr-24 focus:outline-none focus:ring-1 focus:ring-primary"
          data-testid="saved-search-input"
        />
        {q && (
          <button
            onClick={() => setQ("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
            data-testid="saved-search-clear"
          >
            Clear
          </button>
        )}
      </div>
      {loading ? (
        <p className="mt-8 text-sm text-muted-foreground">Loading…</p>
      ) : bookmarks.length === 0 ? (
        <div className="mt-10 text-center text-sm text-muted-foreground" data-testid="saved-empty">
          <Bookmark className="h-6 w-6 mx-auto text-muted-foreground/60" />
          {q ? (
            <>
              <p className="mt-3">No saved messages match your search.</p>
              <p className="mt-1 text-xs">Try a different keyword or clear the search to see everything.</p>
            </>
          ) : (
            <>
              <p className="mt-3">No saved messages yet.</p>
              <p className="mt-1 text-xs">Save a Circle message with the bookmark icon to revisit it here later.</p>
            </>
          )}
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {bookmarks.map((b) => (
            <li key={b.id} className="rounded-2xl border border-border bg-secondary/40 p-4" data-testid={`saved-bookmark-${b.id}`}>
              <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-primary/70">
                    {b.circle_name || "Circle"}
                    {b.parent_message_id && <span className="ml-2">· thread reply</span>}
                  </p>
                  {b.unavailable ? (
                    <p className="mt-1 flex items-center gap-1.5 text-xs italic text-muted-foreground">
                      <ShieldOff className="h-3 w-3" /> You no longer have access to this Circle.
                    </p>
                  ) : b.moderation_removed ? (
                    <p className="mt-1 text-xs italic text-muted-foreground">Message removed by moderation.</p>
                  ) : b.is_deleted ? (
                    <p className="mt-1 text-xs italic text-muted-foreground">Message deleted by author.</p>
                  ) : (
                    <>
                      <p className="mt-1 text-sm font-medium">{b.sender_display}</p>
                      <p className="mt-1 whitespace-pre-wrap text-[15px] leading-relaxed">{b.body}</p>
                      {b.message_created_at && (
                        <p className="mt-1 text-[10px] text-muted-foreground">
                          {new Date(b.message_created_at).toLocaleString()}
                        </p>
                      )}
                    </>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => remove(b.message_id)}
                  className="rounded-full shrink-0"
                  data-testid={`saved-remove-${b.id}`}
                >
                  <BookmarkX className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              </div>
              {!b.unavailable && !b.moderation_removed && !b.is_deleted && (
                <Link
                  to={`/messages?tab=circles&circle=${b.circle_id}&msg=${b.message_id}`}
                  className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-primary hover:underline"
                  data-testid={`saved-open-${b.id}`}
                >
                  <MessageCircle className="h-3 w-3" /> Open in Circle
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
